import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonGroup, Card, IconButton, TextField, Typography, FormControlLabel, Switch } from '@mui/material';
import { Add, ArrowBack, ArrowDownward, ArrowForward, ArrowUpward, Cancel, Clear, Save } from '@mui/icons-material';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';
import { objectToArray } from '../utils';

export default function LayoutEdit({ item, initialLayout, colors, onCancel, onSaveProperty }) {
  const [name, setName] = useState(initialLayout.name);
  const [layoutSteps, setLayoutSteps] = useState(initialLayout.steps || []);
  const [numrows, setNumRows] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // New: layout mode, default to 'copy' if not specified in initialLayout
  const [referenceMotifs, setReferenceMotifs] = useState(initialLayout.referenceMotifs || false);

  const handleSave = () => {
    onSaveProperty('colors', colors);
    // Save steps as either references or copies
    if (referenceMotifs) {
      // Save only motif_id in each step
      const stepsWithRefs = layoutSteps.map(row => ({
        steps: row.steps.map(step => ({ motif_id: step.motif_id || step.id }))
      }));
      onSaveProperty('steps', stepsWithRefs);
    } else {
      // Save full motif data (copy)
      onSaveProperty('steps', layoutSteps);
    }
    onSaveProperty('referenceMotifs', referenceMotifs);
    onSaveProperty('numrows', numrows);
    onCancel();
  };

  useEffect(() => {
    let _numrows = layoutSteps.length;
    setNumRows(_numrows);
  }, [layoutSteps]);

  const handleDrop = (e, rowIndex, colIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    try {
      const droppedData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const { fromLayout, ...itemData } = droppedData;

      let newLayoutSteps = layoutSteps.map((row) => ({
        ...row,
        steps: row.steps.map((step) => ({ ...step }))
      }));

      // Determine what to store in the step: motif reference or copy
      let stepToAdd;
      if (referenceMotifs) {
        // Store only motif_id
        stepToAdd = { motif_id: itemData.id };
      } else {
        // Store full motif data
        stepToAdd = { ...itemData };
      }

      if (rowIndex === layoutSteps.length) {
        // Adding to a new row
        newLayoutSteps.push({ steps: [stepToAdd] });
      } else {
        if (fromLayout) {
          // Remove from previous location
          if (referenceMotifs) {
            const dragRowIndex = newLayoutSteps.findIndex((row) => row.steps.some((step) => step.motif_id === itemData.id));
            const dragColIndex = newLayoutSteps[dragRowIndex].steps.findIndex((step) => step.motif_id === itemData.id);
            newLayoutSteps[dragRowIndex].steps.splice(dragColIndex, 1);
          } else {
            const dragRowIndex = newLayoutSteps.findIndex((row) => row.steps.some((step) => step.id === itemData.id));
            const dragColIndex = newLayoutSteps[dragRowIndex].steps.findIndex((step) => step.id === itemData.id);
            newLayoutSteps[dragRowIndex].steps.splice(dragColIndex, 1);
          }
        }
        newLayoutSteps[rowIndex].steps.splice(colIndex, 0, stepToAdd);
      }

      setLayoutSteps(newLayoutSteps);
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const handleDragStart = (e, item, fromLayout = false) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ ...item, fromLayout }));
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, rowIndex, colIndex) => {
    e.preventDefault();
    setDragOverIndex({ rowIndex, colIndex });
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const addPart = (step) => {
    let newLayoutSteps = [...layoutSteps];
    if (referenceMotifs) {
      newLayoutSteps.push({ steps: [{ motif_id: step.id }] });
    } else {
      newLayoutSteps.push({ steps: [{ ...step }] });
    }
    setLayoutSteps(newLayoutSteps);
  };

  const removePart = (rowIndex, colIndex) => {
    let newLayoutSteps = layoutSteps.map((row) => ({
      ...row,
      steps: row.steps.map((step) => ({ ...step }))
    }));
    newLayoutSteps[rowIndex].steps.splice(colIndex, 1);
    if (newLayoutSteps[rowIndex].steps.length === 0) {
      newLayoutSteps.splice(rowIndex, 1);
    }
    setLayoutSteps(newLayoutSteps);
  };

  const movePartUp = (rowIndex, colIndex) => {
    if (rowIndex > 0) {
      let newLayoutSteps = layoutSteps.map((row) => ({
        ...row,
        steps: row.steps.map((step) => ({ ...step }))
      }));
      const step = newLayoutSteps[rowIndex].steps.splice(colIndex, 1)[0];
      newLayoutSteps[rowIndex - 1].steps.push(step);
      setLayoutSteps(newLayoutSteps);
    }
  };

  const movePartDown = (rowIndex, colIndex) => {
    if (rowIndex < layoutSteps.length - 1) {
      let newLayoutSteps = layoutSteps.map((row) => ({
        ...row,
        steps: row.steps.map((step) => ({ ...step }))
      }));
      const step = newLayoutSteps[rowIndex].steps.splice(colIndex, 1)[0];
      newLayoutSteps[rowIndex + 1].steps.unshift(step);
      setLayoutSteps(newLayoutSteps);
    }
  };

  const movePartLeft = (rowIndex, colIndex) => {
    if (colIndex > 0) {
      let newLayoutSteps = layoutSteps.map((row) => ({
        ...row,
        steps: row.steps.map((step) => ({ ...step }))
      }));
      const step = newLayoutSteps[rowIndex].steps.splice(colIndex, 1)[0];
      newLayoutSteps[rowIndex].steps.splice(colIndex - 1, 0, step);
      setLayoutSteps(newLayoutSteps);
    }
  };

  const movePartRight = (rowIndex, colIndex) => {
    if (colIndex < layoutSteps[rowIndex].steps.length - 1) {
      let newLayoutSteps = layoutSteps.map((row) => ({
        ...row,
        steps: row.steps.map((step) => ({ ...step }))
      }));
      const step = newLayoutSteps[rowIndex].steps.splice(colIndex, 1)[0];
      newLayoutSteps[rowIndex].steps.splice(colIndex + 1, 0, step);
      setLayoutSteps(newLayoutSteps);
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 2, my: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Name
          </Typography>
          <TextField value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
          <FormControlLabel
            control={
              <Switch
                checked={referenceMotifs}
                onChange={(_, checked) => {
                  setReferenceMotifs(checked);
                  // Convert layoutSteps to new mode
                  if (checked) {
                    // Convert all steps to motif references
                    setLayoutSteps(layoutSteps.map(row => ({
                      steps: row.steps.map(step => ({ motif_id: step.motif_id || step.id }))
                    })));
                  } else {
                    // Convert all steps to motif copies
                    setLayoutSteps(layoutSteps.map(row => ({
                      steps: row.steps.map(step => {
                        const motif = item.motifs && (step.motif_id ? item.motifs[step.motif_id] : null);
                        return motif ? { ...motif } : { ...step };
                      })
                    })));
                  }
                }}
                color="primary"
              />
            }
            label="Reference motifs by id (do not copy)"
            sx={{ mt: 2 }}
          />
          <ButtonGroup size="small" sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSave} startIcon={<Save />}>
              Save
            </Button>
            <Button variant="outlined" onClick={onCancel} startIcon={<Cancel />}>
              Cancel
            </Button>
          </ButtonGroup>
        </Box>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Rows: {numrows} &nbsp; Motifs: {layoutSteps.reduce((acc, row) => acc + (row.steps ? row.steps.length : 0), 0)}
        </Typography>
      </Card>
      <Box sx={styles.container}>
        <Box sx={{ ...styles.panel, ...styles.availableStepsPanel }}>
          <Typography variant="h6" sx={styles.heading}>
            Available Motifs
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {item.motifs &&
              objectToArray(item.motifs).map((step, i) => (
                <Box
                  key={i}
                  draggable="true"
                  onDragStart={(e) => handleDragStart(e, step)}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => setHoveredStep(step.id)} // Set hovered step
                  onMouseLeave={() => setHoveredStep(null)} // Clear hovered step
                  sx={{
                    ...styles.availableStep,
                    ...(hoveredStep === step.id ? styles.stepHover : {}),
                    ...(draggedItem?.id === step.id ? styles.dragging : {})
                  }}
                >
                  <Box>
                    {step.name}
                    <ColorChartThumbnail data={step.data} mincols={24} colors={step.colors} norepeat={step.norepeat} />
                  </Box>
                  <IconButton onClick={() => addPart(step)}>
                    <Add />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        <Box sx={{ ...styles.panel, ...styles.layoutPanel }}>
          <Typography variant="h6" sx={styles.heading}>
            {name}
          </Typography>
          <Box>
            {layoutSteps.map((row, rowIndex) => (
              <Box key={`row-${rowIndex}`} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                  {row.steps &&
                    row.steps.map((step, colIndex) => {
                      // If referencing, resolve motif data from item.motifs
                      let motif = null;
                      if (referenceMotifs && step.motif_id && item.motifs) {
                        motif = item.motifs[step.motif_id];
                      }
                      const displayStep = motif ? motif : step;
                      return (
                        <Box
                          key={`${step.motif_id || step.id}-${colIndex}`}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              ...styles.dropZone,
                              ...(dragOverIndex?.rowIndex === rowIndex && dragOverIndex?.colIndex === colIndex ? styles.dragOver : {})
                            }}
                            onDragOver={(e) => handleDragOver(e, rowIndex, colIndex)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, rowIndex, colIndex)}
                          />
                          <Box
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, displayStep, true)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              ...styles.step,
                              ...(hoveredStep === (displayStep.id) ? styles.stepHover : {}),
                              ...(draggedItem?.id === (displayStep.id) ? styles.dragging : {})
                            }}
                          >
                            <ColorChartThumbnail
                              data={displayStep.data}
                              mincols={24}
                              size={2}
                              colors={colors ? colors : displayStep.colors}
                              norepeat={displayStep.norepeat}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton onClick={() => movePartUp(rowIndex, colIndex)}>
                                <ArrowUpward />
                              </IconButton>
                              <IconButton onClick={() => removePart(rowIndex, colIndex)}>
                                <Clear />
                              </IconButton>
                              <IconButton onClick={() => movePartDown(rowIndex, colIndex)}>
                                <ArrowDownward />
                              </IconButton>
                              <IconButton onClick={() => movePartLeft(rowIndex, colIndex)}>
                                <ArrowBack />
                              </IconButton>
                              <IconButton onClick={() => movePartRight(rowIndex, colIndex)}>
                                <ArrowForward />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  <Box
                    sx={{
                      ...styles.finalDropZone,
                      ...(dragOverIndex?.rowIndex === rowIndex && dragOverIndex?.colIndex === row.steps.length ? styles.dragOver : {})
                    }}
                    onDragOver={(e) => handleDragOver(e, rowIndex, row.steps.length)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, rowIndex, row.steps.length)}
                  >
                    Drop here to add motif
                  </Box>
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                ...styles.finalDropZone,
                ...(dragOverIndex?.rowIndex === layoutSteps.length ? styles.dragOver : {})
              }}
              onDragOver={(e) => handleDragOver(e, layoutSteps.length, 0)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, layoutSteps.length, 0)}
            >
              Drop here to add new row
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const styles = {
  container: {
    display: 'flex',
    gap: 2
  },
  panel: {
    backgroundColor: '#f3f4f6',
    padding: 2,
    borderRadius: '0.375rem'
  },
  availableStepsPanel: {
    width: 180
  },
  layoutPanel: {
    flex: 1,
    minHeight: '24rem'
  },
  heading: {
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '1rem'
  },
  step: {
    backgroundColor: 'white',
    cursor: 'move',
    transition: 'background-color 0.2s',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: 0
  },
  availableStep: {
    padding: 12,
    backgroundColor: 'white',
    borderRadius: '0.375rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    cursor: 'move',
    transition: 'background-color 0.2s',
    display: 'flex',
    justifyContent: 'space-between'
  },
  stepHover: {
    backgroundColor: '#f9fafb'
  },
  dragging: {
    opacity: 0.5,
    backgroundColor: '#f9fafb'
  },
  dragOver: {
    backgroundColor: 'green'
  },
  dropZone: {
    height: 10,
    width: '100%'
  },
  finalDropZone: {
    height: '6rem',
    border: '2px dashed #d1d5db',
    borderRadius: '0.375rem',
    marginTop: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af'
  }
};
